import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { OrderCreationLayout } from '@/components/order/OrderCreationLayout'
import { getCategories } from '@/services/orderApi'
import { useOrderCreationStore } from '@/store/orderCreationStore'
import { useLocalization } from '@/contexts/LocalizationContext'
import { useProfileSwitch } from '@/contexts/ProfileSwitchContext'
import { useAuthStore } from '@/store/authStore'
import { AlertCircle, FolderOpen, ArrowRight, User } from 'lucide-react'
import type { Category } from '@/services/orderApi'

export function CategorySelectionPage() {
  const navigate = useNavigate()
  const { locale, t } = useLocalization()
  const {
    clientProfileId,
    businessClientProfileId,
    clientName,
    setSelectedCategory,
    setCurrentStep,
    setBusinessClientSelfOrder,
    getCreateOrderEntryPath,
  } = useOrderCreationStore()
  const { isBusinessClientMode, businessClientProfile } = useProfileSwitch()
  const user = useAuthStore((s) => s.user)
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const hasSubject = !!(clientProfileId || businessClientProfileId)
    if (
      !hasSubject &&
      isBusinessClientMode &&
      businessClientProfile?.profile_id
    ) {
      setBusinessClientSelfOrder(
        businessClientProfile.profile_id,
        businessClientProfile.display_name ?? '',
        user?.phone_number ?? null,
        user?.email ?? null,
        businessClientProfile.business_account_id ?? null
      )
      return
    }
    if (!hasSubject) {
      if (isBusinessClientMode && !businessClientProfile?.profile_id) {
        navigate('/dashboard', { replace: true })
        return
      }
      navigate(getCreateOrderEntryPath(), { replace: true })
      return
    }
    loadCategories()
  }, [
    businessClientProfile,
    businessClientProfile?.business_account_id,
    businessClientProfileId,
    clientProfileId,
    getCreateOrderEntryPath,
    isBusinessClientMode,
    navigate,
    setBusinessClientSelfOrder,
    user?.email,
    user?.phone_number,
  ])

  const loadCategories = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getCategories()
      setCategories(data)
    } catch (err: any) {
      setError(err.message || t('orderCreation.errorLoadingCategories'))
      console.error('Error loading categories:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectCategory = (category: Category) => {
    setSelectedCategory(category)
    setCurrentStep(3)
    navigate('/create-order/templates')
  }

  const getCategoryName = (category: Category) => {
    return locale === 'ar' ? category.nameAr : category.nameEn
  }

  const getCategoryDescription = (category: Category) => {
    return locale === 'ar' ? category.descriptionAr : category.descriptionEn
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )
    }

    if (error) {
      return (
        <Card className="border-destructive">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <CardTitle>
                {t('orderCreation.errorLoadingCategories')}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={loadCategories}>
              {t('common.retry')}
            </Button>
          </CardContent>
        </Card>
      )
    }

    if (categories.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <FolderOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              {t('orderCreation.noCategoriesAvailable')}
            </p>
          </CardContent>
        </Card>
      )
    }

    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {categories.map((category) => (
          <Card
            key={category.id}
            className="cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all group"
            onClick={() => handleSelectCategory(category)}
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{getCategoryName(category)}</CardTitle>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>
              {getCategoryDescription(category) && (
                <CardDescription className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                  {getCategoryDescription(category)}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <Button className="w-full" variant="outline">
                {t('orderCreation.viewTemplates')}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <OrderCreationLayout
      titleKey="orderCreation.selectServiceCategory"
      descriptionKey="orderCreation.selectServiceCategoryDescription"
      step={2}
      backPath={businessClientProfileId ? '/dashboard' : '/create-order'}
    >
      <div className="max-w-5xl mx-auto">
        {/* Selected Client Info */}
        {clientName && (
          <Card className="mb-6 bg-muted/30">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {businessClientProfileId
                      ? t('orderCreation.orderingAsYourself')
                      : t('orderCreation.selectedClient')}
                  </p>
                  <p className="font-semibold">{clientName}</p>
                </div>
                <Badge variant="secondary" className="ms-auto">
                  {t('orderCreation.stepOf', { step: 2, total: 7 })}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {renderContent()}
      </div>
    </OrderCreationLayout>
  )
}
